import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from '../../util/reactIntl';
import { AddImages, ValidationError, AddImagesField, CircleProgressBar } from '../../components';
import { StrategySelected as Strategy } from '../../components/UploadImageWizard/modals/StrategyModal';
import { StrategiesListModal as List } from '../../components/UploadImageWizard/modals/StrategiesListModal';
import { UploadImageWizard } from '../../components/UploadImageWizard/UploadImageWizard';
import { Alert } from '../../components';
import css from './EditListingPhotosForm.css';
import { STEP_UPLOAD_PIC_LIST } from '../../components/UploadImageWizard/helpers';
import { blobsToFileList, blobToFile, convertSingleFileToMult } from '../../util/file';
import { IconCameraL } from '../../icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSelector } from 'react-redux';
import { getAssetErrorMessageFromCode } from '../../util/errors';

const EditListingPhotosPageOne = ({
    imageUploadRequested,
    dragOverFORM,
    setImageIdDragged,
    uploadAssetsInProgress,
    setImageUploadRequested,
    onArrangingGalleryImages,
    onRemovingGalleryAssets,
    onImageUploadHandler,
    currentListing,
    images,
    assets,
    values,
    onSubmit,
    fetchErrors,
    noAssets,
    form,
}) => {
    const [isMobile] = useIsMobile(1024);

    const { uuid: listingId } = currentListing.id;

    const listingAssetsUploadError = useSelector(s => {
        const errors = s.Assets.assetsErrors;
        return errors && errors[listingId];
    });

    const hasVideo = (images || []).some(({ type }) => type === 'video');

    const { publishListingError, showListingsError, uploadImageError } = fetchErrors || {};

    const arrangeGalleryImages = async ({ images }, values) => {
        const { isSuccessful, hasVideo, assets: assetsArranged } = await onArrangingGalleryImages({
            images,
            assets,
            entityId: listingId,
        });
        if (isSuccessful) {
            onSubmit({
                hasVideo,
                hasGalleryAssets: !!images.length,
                abortRedirect: true,
                isAutoSave: true,
                ...values,
                mainListingImage: Date.now(),
            });
        }
        setImageUploadRequested(false);
    };

    const onImageRemoveHandler = async ({ uuid: idToDelete }, values) => {
        setImageUploadRequested(true);

        const { isSuccessful, assets } = await onRemovingGalleryAssets({
            entityId: listingId,
            idToDelete,
            images,
        });

        if (!isSuccessful) {
            return setImageUploadRequested(false);
        }

        await arrangeGalleryImages({ images: assets }, values);

        return true;
    };

    const uploadImageFailed = uploadImageError ? (
        <p className={css.error}>
            <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
        </p>
    ) : null;

    const publishListingFailed = publishListingError ? (
        <p className={css.error}>
            <FormattedMessage id="EditListingPhotosForm.publishListingFailed" />
        </p>
    ) : null;
    const showListingFailed = showListingsError ? (
        <p className={css.error}>
            <FormattedMessage id="EditListingPhotosForm.showListingFailed" />
        </p>
    ) : null;

    const onFilesUpload = (files, { setStrategy, setUploadState }) => {
        for (let file of files) {
            form.change(`addImage`, file);
        }
        setStrategy(null);
        setUploadState(null);

        return onImageUploadHandler(files, values);
    };
    const uploadFbPic = (blobs, setters) =>
        blobsToFileList(blobs, files => onFilesUpload(files, setters));

    const uploadCameraPhoto = (blob, setters) =>
        convertSingleFileToMult(blobToFile(blob).get('file'), files =>
            onFilesUpload(files, setters)
        );

    const fbStrategyProps = {
        isMultiple: true,
        onImageUpload: uploadFbPic,
        inputProps: { maxLength: 10 },
    };

    const imagesAssets = images.filter(img => img.type === 'image');

    return (
        <>
            <div className={css.title}>
                <h1 className={css.uploadHorseTitle}>
                    <FormattedMessage id="EditListingPhotosForm.uploadHorseTitle" />
                </h1>
                <div className={css.uploadHorseSubTitle}>
                    <FormattedMessage id="EditListingPhotosForm.uploadHorseSubTitle" />
                </div>
                <div className={css.progressWrapper}>
                    <CircleProgressBar percentage={imagesAssets.length * 25} />
                    <div className={css.progressText}>
                        <FormattedMessage
                            id={
                                4 - imagesAssets.length > 0
                                    ? 'EditListingPhotosForm.firstProgressText'
                                    : 'EditListingPhotosForm.firstProgressCompletedText'
                            }
                            values={{ number: 4 - imagesAssets.length }}
                        />
                    </div>
                    <div className={css.progressPercentage}>
                        <FormattedMessage id="EditListingPhotosForm.firstProgressPercentageText" />
                    </div>
                </div>
                <div className={css.progressWrapperSecond}>
                    <CircleProgressBar percentage={hasVideo ? 100 : 0} />
                    <div className={css.progressText}>
                        <FormattedMessage
                            id={`EditListingPhotosForm.secondProgressText${
                                hasVideo ? '-success' : ''
                            }`}
                        />
                    </div>
                    <div className={css.progressPercentage}>
                        <FormattedMessage id="EditListingPhotosForm.secondProgressPercentageText" />
                    </div>
                </div>
            </div>

            {listingAssetsUploadError && (
                <Alert
                    rootClassName={css.errorAlert}
                    closeClassName={css.errorAlertCloseCTA}
                    closeAllowed
                    type="failure"
                >
                    <p>
                        {typeof listingAssetsUploadError === 'object' &&
                        listingAssetsUploadError.code
                            ? getAssetErrorMessageFromCode(listingAssetsUploadError.code)
                            : 'Something went wrong. Please try again later.'}
                    </p>
                </Alert>
            )}

            <AddImages
                useControls
                entityId={listingId}
                className={css.red}
                images={images}
                loading={uploadAssetsInProgress}
                videoControls={false}
                abortRedirect={true}
                initialState={noAssets}
                savedImageAltText="Gespeichertes Angebotsbild"
                onRemoveImage={data => onImageRemoveHandler(data, values)}
                onSubmit={images => arrangeGalleryImages(images, values)}
                notifyOnImageDragOver={imgId => setImageIdDragged(imgId)}
                notifyOnImageRotatingSuccess={() =>
                    onSubmit({ ...values, mainListingImage: Date.now(), abortRedirect: true })
                }
                wrapperClassName={css.threeToTwoWrapper}
            >
                <UploadImageWizard
                    uploadInProgress={uploadAssetsInProgress}
                    imageName="galleryFile"
                    onImageUpload={convertSingleFileToMult}
                    renderPlaceholder={props => (
                        <AddImagesField
                            name="addImage"
                            initialState={noAssets}
                            dragOverFORM={dragOverFORM}
                            disabled={imageUploadRequested || uploadAssetsInProgress}
                            inProgress={uploadAssetsInProgress}
                            imagesUploaded={images.length}
                            init={() => props.setUploadState(STEP_UPLOAD_PIC_LIST)}
                            multiple
                        />
                    )}
                >
                    <List uploadInProgress={imageUploadRequested || uploadAssetsInProgress}>
                        <List.Heading>
                            <h3>Fotos und Videos hochladen</h3>
                            <p>Füge Fotos und Videos deines Pferdes hinzu.</p>
                        </List.Heading>
                        <List.Container>
                            {isMobile ? (
                                <List.PcImage
                                    label="Foto oder Video machen"
                                    onPcFileUpload={onFilesUpload}
                                    icon={<IconCameraL />}
                                    key="camera-photo"
                                    fieldName="camera-photo"
                                />
                            ) : (
                                <List.CamerPhoto label="Foto machen" />
                            )}
                            <List.PcImage
                                label="Foto oder Video hochladen"
                                onPcFileUpload={onFilesUpload}
                                isMultiple
                                key="default-upload"
                                fieldName="default-upload"
                            />
                            <List.FacebookPic />
                            {/* <List.InstagramPic /> */}
                        </List.Container>
                    </List>
                    <Strategy>
                        <Strategy.CameraPhoto
                            convertTo="toBlob"
                            onImageUpload={uploadCameraPhoto}
                        />
                        <Strategy.Facebook {...fbStrategyProps} />
                        {/* <Strategy.Instagram {...fbStrategyProps} /> */}
                    </Strategy>
                </UploadImageWizard>
                <Field
                    component={props => {
                        const { input, meta } = props;
                        return (
                            <div className={css.imageRequiredWrapper}>
                                <input {...input} />
                                <ValidationError fieldMeta={meta} />
                            </div>
                        );
                    }}
                    name="images"
                    type="hidden"
                />
            </AddImages>

            {uploadImageFailed}
            {publishListingFailed}
            {showListingFailed}
        </>
    );
};

export default EditListingPhotosPageOne;
